import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createProfileSchema } from "../schema/profile.schema";
import type { ProfileFormData, ProfileData } from "../types";
import { useUpdateProfile } from "../services/UpdateProfile";
import { useUpdateProfileImage } from "../services/UpdateImage";
import { showNotification } from "@libs/notistack/notificationStack";
import { SNACKBAR_SEVERITY } from "@constants/notificationStack";
import { useLogout } from "@hooks/useLogout";

export const useProfileForm = (data: ProfileData | undefined, t: (key: string) => string) => {
	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const [showEmailChangeWarning, setShowEmailChangeWarning] = useState<boolean>(false);
	const [pendingFormData, setPendingFormData] = useState<ProfileFormData | null>(null);
	const { mutate: updateProfile, isPending: isUpdating } = useUpdateProfile();
	const { mutate: updateProfileImage, isPending: isUploading } = useUpdateProfileImage();
	const { handleLogoutConfirm } = useLogout();

	const profileSchema = createProfileSchema(t);
	console.log("DATAA", data);
	const form = useForm<ProfileFormData>({
		resolver: zodResolver(profileSchema),
		defaultValues: {
			firstName: data?.firstName || "",
			lastName: data?.lastName || "",
			phoneNumber: data?.phoneNumber || "",
			emailAddress: data?.emailAddress || "",
		},
	});

	// Reset form when data changes (e.g., after successful update)
	useEffect(() => {
		if (data) {
			form.reset({
				firstName: data.firstName || "",
				lastName: data.lastName || "",
				phoneNumber: data.phoneNumber || "",
				emailAddress: data.emailAddress || "",
			});
		}
	}, [data, form]);

	const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		const file = event.target.files?.[0];
		if (file) {
			updateProfileImage(file);
		}
	};

	const onSubmit = (formData: ProfileFormData): void => {
		console.log("Form submitted with data:", formData);
		console.log("Current data:", data);

		const hasChanges =
			formData.firstName !== data?.firstName ||
			formData.lastName !== data?.lastName ||
			formData.emailAddress !== data?.emailAddress ||
			formData.phoneNumber !== data?.phoneNumber;

		console.log("Has changes:", hasChanges);

		if (!hasChanges) {
			console.log("No changes detected, not submitting");
			setIsEditMode(false);
			return;
		}

		// Check if email has changed
		const emailChanged = formData.emailAddress !== data?.emailAddress;
		
		if (emailChanged) {
			// Show warning for email change
			setPendingFormData(formData);
			setShowEmailChangeWarning(true);
			return;
		}

		console.log("Submitting profile update...");
		updateProfile(formData, {
			onSuccess: response => {
				console.log("Profile update successful:", response);
				setIsEditMode(false);
			},
		});
	};

	const handleCancel = (): void => {
		setIsEditMode(false);
		form.reset({
			firstName: data?.firstName || "",
			lastName: data?.lastName || "",
			emailAddress: data?.emailAddress || "",
			phoneNumber: data?.phoneNumber || "",
		});
	};

	const handleEdit = (): void => {
		setIsEditMode(true);
		// Reset form to current data when entering edit mode
		form.reset({
			firstName: data?.firstName || "",
			lastName: data?.lastName || "",
			emailAddress: data?.emailAddress || "",
			phoneNumber: data?.phoneNumber || "",
		});
	};

	const handleEmailChangeConfirm = (): void => {
		if (!pendingFormData) return;

		// Show notification about email change
		showNotification({
			title: t("psdaCommon.profile.emailChangeWarningModal.modalTitle"),
			message: t("psdaCommon.profile.emailChangeWarningModal.notificationMessage"),
			severity: SNACKBAR_SEVERITY.WARNING,
			autoHideDuration: 5000,
		});

		// Update profile with new email
		updateProfile(pendingFormData, {
			onSuccess: response => {
				console.log("Profile update successful:", response);
				setIsEditMode(false);
				setShowEmailChangeWarning(false);
				setPendingFormData(null);
				
				// Logout user after successful email update
				setTimeout(() => {
					handleLogoutConfirm();
				}, 2000); // Give user time to see the success message
			},
			onError: error => {
				console.error("Profile update failed:", error);
				setShowEmailChangeWarning(false);
				setPendingFormData(null);
			},
		});
	};

	const handleEmailChangeCancel = (): void => {
		setShowEmailChangeWarning(false);
		setPendingFormData(null);
	};

	return {
		isEditMode,
		isUpdating,
		isUploading,
		form,
		handleImageChange,
		onSubmit,
		handleCancel,
		handleEdit,
		showEmailChangeWarning,
		handleEmailChangeConfirm,
		handleEmailChangeCancel,
	};
};
